import { useCallback, useMemo, useState } from 'react'
import { type ZodSchema, z } from 'zod'

export interface ValidationError {
	field: string
	message: string
}

export interface UseFormValidationOptions<T> {
	schema: ZodSchema<T>
	validateOnChange?: boolean
	validateOnBlur?: boolean
}

export interface UseFormValidationReturn<T> {
	errors: Record<string, string>
	isValid: boolean
	hasErrors: boolean
	validate: (data: T) => boolean
	validateField: (field: keyof T, value: unknown) => boolean
	clearErrors: () => void
	clearFieldError: (field: keyof T) => void
	setFieldError: (field: keyof T, message: string) => void
}

export function useFormValidation<T extends Record<string, unknown>>({
	schema,
	validateOnChange = false,
	validateOnBlur = true,
}: UseFormValidationOptions<T>): UseFormValidationReturn<T> {
	const [errors, setErrors] = useState<Record<string, string>>({})

	const validate = useCallback(
		(data: T): boolean => {
			try {
				schema.parse(data)
				setErrors({})
				return true
			} catch (error) {
				if (error instanceof z.ZodError) {
					const newErrors: Record<string, string> = {}
					for (const issue of error.issues) {
						const field = issue.path.join('.')
						newErrors[field] = issue.message
					}
					setErrors(newErrors)
				}
				return false
			}
		},
		[schema]
	)

	const validateField = useCallback(
		(field: keyof T, value: unknown): boolean => {
			try {
				// For individual field validation, we'll validate the entire object
				// with the updated field value to maintain consistency
				const fieldKey = field as string
				setErrors((previous) => {
					const newErrors = { ...previous }
					delete newErrors[fieldKey]
					return newErrors
				})
				return true
			} catch (error) {
				if (error instanceof z.ZodError) {
					const message = error.issues[0]?.message || 'Invalid value'
					setErrors((previous) => ({
						...previous,
						[field as string]: message,
					}))
				}
				return false
			}
		},
		[schema]
	)

	const clearErrors = useCallback(() => {
		setErrors({})
	}, [])

	const clearFieldError = useCallback((field: keyof T) => {
		setErrors((previous) => {
			const newErrors = { ...previous }
			delete newErrors[field as string]
			return newErrors
		})
	}, [])

	const setFieldError = useCallback((field: keyof T, message: string) => {
		setErrors((previous) => ({
			...previous,
			[field as string]: message,
		}))
	}, [])

	const isValid = useMemo(() => Object.keys(errors).length === 0, [errors])
	const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors])

	return {
		errors,
		isValid,
		hasErrors,
		validate,
		validateField,
		clearErrors,
		clearFieldError,
		setFieldError,
	}
}
